// ---------------------------------------------------------------------------
// PWA_ControlBillet — useProtocolHandler hook
// Port of protocol-handler.js as a React hook.
// Handles web+sideview:// deep links and URL parameter actions.
// ---------------------------------------------------------------------------

import { useEffect, useCallback } from 'react'
import { useAppStore, type Screen } from '@/store/app-store'
import { useConfigStore } from '@/store/config-store'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PROTOCOL_PREFIX = 'web+sideview://'

/** Whitelist of allowed URL parameters (XSS prevention -- CVSS 7.3 fix). */
const ALLOWED_PARAMS = ['ticket', 'event', 'date', 'action', 'vigile'] as const

// ---------------------------------------------------------------------------
// Sanitizer
// ---------------------------------------------------------------------------

/**
 * Sanitize user input to prevent XSS attacks.
 * CRITICAL SECURITY METHOD (CVSS 7.3 fix).
 */
function sanitizeInput(input: string): string {
  let sanitized = input

  // Remove HTML tags
  sanitized = sanitized.replace(/<[^>]*>/g, '')

  // Remove script: and javascript: protocols
  sanitized = sanitized.replace(/javascript:/gi, '')
  sanitized = sanitized.replace(/script:/gi, '')

  // Remove event handlers (onclick, onerror, etc.)
  sanitized = sanitized.replace(/on\w+\s*=/gi, '')

  // Encode special HTML characters
  const entityMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;',
  }

  sanitized = sanitized.replace(/[&<>"'/]/g, (char) => entityMap[char] ?? char)

  return sanitized
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * React hook that handles protocol-based deep linking for the PWA.
 *
 * On mount it inspects URL query params and the hash for:
 * - `?ticket=...` -- trigger ticket validation
 * - `?action=scan|history|validate` -- navigate to the appropriate screen
 * - `#web+sideview://action?params` -- protocol handler format
 *
 * It also listens for `hashchange` events.
 *
 * @param onValidateTicket - Callback invoked when a ticket needs validation.
 *                           Receives `{ ticket, event?, date? }`.
 */
export function useProtocolHandler(
  onValidateTicket?: (params: { ticket: string; event?: string; date?: string }) => void,
): void {
  const navigate = useAppStore((s) => s.navigate)

  const handleProtocol = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search)

    // Check for ?ticket= parameter (from manifest protocol_handlers)
    const ticketParam = urlParams.get('ticket')
    if (ticketParam) {
      const sanitized = sanitizeInput(ticketParam)
      const event = urlParams.get('event')
      const date = urlParams.get('date')
      onValidateTicket?.({
        ticket: sanitized,
        event: event ? sanitizeInput(event) : undefined,
        date: date ? sanitizeInput(date) : undefined,
      })
      return
    }

    // Check for ?action= parameter
    const action = urlParams.get('action')
    if (action) {
      const sanitizedAction = sanitizeInput(action)
      handleAction(sanitizedAction, urlParams, navigate, onValidateTicket)
      return
    }

    // Check hash for web+sideview:// URL
    if (window.location.hash.startsWith(`#${PROTOCOL_PREFIX}`)) {
      const protocolUrl = window.location.hash.substring(1)
      handleProtocolUrl(protocolUrl, navigate, onValidateTicket)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, onValidateTicket])

  useEffect(() => {
    // Check on mount
    handleProtocol()

    // Listen for hash changes
    const onHashChange = () => handleProtocol()
    window.addEventListener('hashchange', onHashChange)

    return () => {
      window.removeEventListener('hashchange', onHashChange)
    }
  }, [handleProtocol])
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function handleAction(
  action: string,
  urlParams: URLSearchParams,
  navigate: (screen: Screen) => void,
  onValidateTicket?: (params: { ticket: string; event?: string; date?: string }) => void,
): void {
  switch (action) {
    case 'validate': {
      const ticket = urlParams.get('ticket')
      if (ticket) {
        const event = urlParams.get('event')
        const date = urlParams.get('date')
        onValidateTicket?.({
          ticket: sanitizeInput(ticket),
          event: event ? sanitizeInput(event) : undefined,
          date: date ? sanitizeInput(date) : undefined,
        })
      }
      navigate('scanner')
      break
    }
    case 'scan':
      navigate('scanner')
      break
    case 'history':
      navigate('history')
      break
    default:
      console.warn(`Unsupported action: ${action}`)
  }
}

function handleProtocolUrl(
  url: string,
  navigate: (screen: Screen) => void,
  onValidateTicket?: (params: { ticket: string; event?: string; date?: string }) => void,
): void {
  try {
    if (!url.startsWith(PROTOCOL_PREFIX)) {
      throw new Error('Invalid protocol prefix')
    }

    const urlWithoutProtocol = url.substring(PROTOCOL_PREFIX.length)
    const [actionPath, queryString] = urlWithoutProtocol.split('?')
    const action = actionPath.split('/')[0]

    // Parse and sanitize query parameters
    const params: Record<string, string> = {}
    if (queryString) {
      const urlParams = new URLSearchParams(queryString)
      for (const [key, value] of urlParams) {
        if (!(ALLOWED_PARAMS as readonly string[]).includes(key)) {
          console.warn(`SECURITY: Rejected non-whitelisted parameter: ${key}`)
          continue
        }
        params[key] = sanitizeInput(value)
      }
    }

    // Dispatch by action
    switch (action) {
      case 'validate': {
        if (params.ticket) {
          onValidateTicket?.({
            ticket: params.ticket,
            event: params.event,
            date: params.date,
          })
        }
        navigate('scanner')
        break
      }
      case 'scan':
        navigate('scanner')
        break
      case 'history':
        navigate('history')
        break
      default:
        console.warn(`No handler registered for action: ${action}`)
    }
  } catch (error) {
    console.error('Protocol URL handling error:', error)
  }
}
